import type { TAjaxHeaders, TAjaxOptions } from "@/types";

// Helper functions for making AJAX requests
const ajax = {
	// GET
	async get(url: string, options: TAjaxOptions = {}) {
		// Request headers
		const headers: TAjaxHeaders = {
			Accept: "application/json",
			...(options.headers ?? {}),
		};

		const response: Response = await fetch(url, { ...options, headers });

		if (response.status >= 400) {
			let message = await response.json().catch(() => null);

			if (message && typeof message === "object") {
				message = message.errorMessage;
			}

			// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
			return Promise.reject({
				status: response.status,
				message,
			});
		}

		return response.json();
	},

	// POST
	async post(
		url: string,
		data: Record<string, any>,
		options: TAjaxOptions = {}
	) {
		const isFormData =
			Object.prototype.toString.call(data) === "[object FormData]";

		const response: Response = await fetch(url, {
			method: "POST",
			body: isFormData ? data : JSON.stringify(data),
			...options,
			headers: {
				...(isFormData ? {} : { "Content-Type": "application/json" }),
				...(options.headers ?? {}),
			},
		} as RequestInit);

		if (response.status >= 400) {
			let message: any = await response.json().catch(() => null);

			if (message && typeof message === "object") {
				message = message.errorMessage;
			}

			// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
			return Promise.reject({
				status: response.status,
				message,
			});
		}

		return isFormData ? response.text() : response.json();
	},

	// DELETE
	async delete(
		url: string,
		data?: Record<string, any>,
		options: TAjaxOptions = {}
	) {
		const request: RequestInit = {
			method: "DELETE",
			...options,
			headers: {
				"Content-Type": "application/json",
				...(options.headers ?? {}),
			},
		};

		if (data) {
			request.body = JSON.stringify(data);
		}

		const response: Response = await fetch(url, request);

		if (response.status >= 400) {
			let message: any = await response.json().catch(() => null);

			if (message && typeof message === "object") {
				message = message.errorMessage;
			}

			// eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
			return Promise.reject({
				status: response.status,
				message,
			});
		}

		return Promise.resolve(null);
	},
};

export default ajax;
